/**
 * Composer del chat con IA del admin: textarea que crece con el texto (hasta unas 6 líneas),
 * Enter envía y Shift + Enter salta de línea, contador al acercarse al tope y una línea de pie
 * (por omisión, la ayuda del teclado; la guía pone ahí las preguntas que quedan en la hora).
 *
 * Slots: `arriba` (p. ej. modo Rápido/Profundo y Detener del CEO), `accesorio` (botón dentro del
 * cuadro, p. ej. el dictado) y `debajo` (notas).
 */
import { useEffect, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { Loader2, SendHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconTip } from "@/components/ui/icon-tip";
import { cn } from "@/lib/utils";
import { mostrarContador } from "@/lib/chatIa";

/** Alto máximo del input antes de hacer scroll (unas 6 líneas). */
const ALTO_MAX_INPUT = 160;

export interface ChatIaComposerProps {
  valor: string;
  onCambiar: (valor: string) => void;
  onEnviar: () => void;
  /** No se puede escribir (límite, apagado). */
  bloqueado?: boolean;
  /** Se está respondiendo: se puede escribir, no enviar. */
  enviando?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  maxLength?: number;
  /** Texto del pie a la izquierda; por omisión, la ayuda del teclado. */
  pie?: ReactNode;
  arriba?: ReactNode;
  accesorio?: ReactNode;
  debajo?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement>;
}

export function ChatIaComposer({
  valor,
  onCambiar,
  onEnviar,
  bloqueado,
  enviando,
  placeholder = "Escribe tu pregunta",
  ariaLabel = "Tu pregunta para el asistente",
  maxLength,
  pie,
  arriba,
  accesorio,
  debajo,
  inputRef,
}: ChatIaComposerProps) {
  const puedeEnviar = !bloqueado && !enviando && valor.trim().length > 0;

  // El input crece con el texto hasta ALTO_MAX_INPUT.
  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, ALTO_MAX_INPUT)}px`;
  }, [valor, inputRef]);

  const alTeclear = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (puedeEnviar) onEnviar();
  };

  return (
    <div className="border-t border-border bg-background px-4 pb-3 pt-3">
      {arriba && <div className="mb-2 flex flex-wrap items-center gap-1">{arriba}</div>}
      <div
        className={cn(
          "flex items-end gap-2 rounded-2xl border border-input bg-background py-1.5 pl-3.5 pr-1.5 shadow-sm transition-colors",
          "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20",
          bloqueado && "bg-muted/40",
        )}
      >
        <textarea
          ref={inputRef}
          value={valor}
          onChange={(e) => onCambiar(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
          onKeyDown={alTeclear}
          maxLength={maxLength}
          rows={1}
          disabled={bloqueado}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="max-h-40 min-h-[36px] min-w-0 flex-1 resize-none bg-transparent py-2 text-[13.5px] leading-5 text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
        />
        {accesorio}
        <IconTip label="Enviar">
          <Button
            type="button"
            size="icon"
            aria-label="Enviar pregunta"
            disabled={!puedeEnviar}
            onClick={onEnviar}
            className="size-9 shrink-0 rounded-xl"
          >
            {enviando ? <Loader2 className="animate-spin" /> : <SendHorizontal />}
          </Button>
        </IconTip>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 px-1 text-[11px] text-muted-foreground">
        <span className="min-w-0">{pie ?? <span className="hidden sm:inline">Enter para enviar, Shift + Enter para otra línea</span>}</span>
        {maxLength && mostrarContador(valor.length, maxLength) && (
          <span className={cn("shrink-0 tabular-nums", valor.length >= maxLength && "text-destructive")}>
            {valor.length}/{maxLength}
          </span>
        )}
      </div>
      {debajo}
    </div>
  );
}
